import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Notification } from "modules/chat/model/chat";
import { useFetchWrapper } from "modules/common/hooks/useFetchWrapper";
import { useAuth } from "@clerk/nextjs";
import { QUERIES } from "modules/common/query";
import { API_ROUTES } from "utils/api-route.constants";
import {
  notificationOptions,
  type NotificationData,
} from "modules/notifications/queries/notificationQueries";
import { captureError } from "utils/error-sanitizer";

export const useNotification = () => {
  const { userId } = useAuth();
  const fetch = useFetchWrapper();
  const queryClient = useQueryClient();

  // All derived values are now computed in select - fully memoized by React Query
  const { data } = useQuery(
    notificationOptions({
      userId: userId || "",
      fetchWrapper: fetch,
    })
  );

  // Provide default empty state if data is undefined
  const notificationData: NotificationData = data ?? {
    notifications: [],
    chatNotifications: [],
    tradeNotifications: [],
    proposalNotifications: [],
    unreadNotifications: [],
    unreadChatNotifications: [],
    unreadTradeNotifications: [],
    unreadProposalNotifications: [],
    unreadCount: 0,
    hasUnreadNotifications: false,
    hasUnreadChatNotifications: false,
    hasUnreadTradeNotifications: false,
    hasUnreadProposalNotifications: false,
    latestChatNotification: null,
    unreadCountsByChatId: {},
    notificationCounts: { total: 0, unread: 0, chat: 0, trade: 0, proposal: 0 },
    notificationGroups: [],
  };

  // Helper functions for backward compatibility
  const getUnreadCount = (chatId: string): number => {
    return notificationData.unreadCountsByChatId[chatId] || 0;
  };

  const hasUnreadMessages = (chatId: string): boolean => {
    return getUnreadCount(chatId) > 0;
  };

  const getTotalUnreadCount = (): number => {
    return Object.values(notificationData.unreadCountsByChatId).reduce(
      (sum, count) => sum + count,
      0
    );
  };
  // Unified mutation for marking notifications as read
  const markNotificationMutation = useMutation({
    mutationFn: async (input: string | string[]) => {
      const notificationIds = Array.isArray(input) ? input : [input];

      if (notificationIds.length === 1) {
        await fetch.patch(API_ROUTES.NOTIFICATIONS.MARK_READ(notificationIds[0]), null);
        return notificationIds;
      } else {
        // Mark all notifications as read in parallel
        await Promise.all(
          notificationIds.map((id) => fetch.patch(API_ROUTES.NOTIFICATIONS.MARK_READ(id), null))
        );
        return notificationIds;
      }
    },
    onMutate: async (input: string | string[]) => {
      const notificationIds = Array.isArray(input) ? input : [input];

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: [QUERIES.NOTIFICATIONS, userId],
      });

      // Snapshot the previous value
      const previousNotifications = queryClient.getQueryData<Notification[]>([
        QUERIES.NOTIFICATIONS,
        userId,
      ]);

      // Optimistically update notifications as read
      queryClient.setQueryData<Notification[]>(
        [QUERIES.NOTIFICATIONS, userId],
        (old) =>
          old?.map((notification) =>
            notificationIds.includes(notification.id)
              ? { ...notification, status: "read" as const }
              : notification
          ) || []
      );

      return { previousNotifications };
    },
    onError: (err, input, context) => {
      const notificationIds = Array.isArray(input) ? input : [input];

      // Rollback on error
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          [QUERIES.NOTIFICATIONS, userId],
          context.previousNotifications
        );
      }

      // Refetch to confirm server state after rollback
      queryClient.invalidateQueries({
        queryKey: [QUERIES.NOTIFICATIONS, userId],
      });

      captureError(err, {
        context: "useNotification.markAsRead",
        notificationIds,
      });
    },
    // No onSettled: optimistic updates handle the success path immediately,
    // and WS notification_updated events drive any server-side re-sync.
    // onSettled would cause redundant refetches after every mark-as-read.
  });

  // Mutation for marking notifications as shown (with optimistic updates)
  const markShownMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      await fetch.patch(API_ROUTES.NOTIFICATIONS.MARK_SHOWN(notificationId), null);
      return notificationId;
    },
    onMutate: async (notificationId: string) => {
      // Cancel any outgoing refetches to prevent overwriting optimistic update
      await queryClient.cancelQueries({
        queryKey: [QUERIES.NOTIFICATIONS, userId],
      });

      // Snapshot the previous value for rollback
      const previousNotifications = queryClient.getQueryData<Notification[]>([
        QUERIES.NOTIFICATIONS,
        userId,
      ]);

      // Optimistically update notification as shown
      queryClient.setQueryData<Notification[]>(
        [QUERIES.NOTIFICATIONS, userId],
        (old) =>
          old?.map((notification) =>
            notification.id === notificationId
              ? { ...notification, shown: true }
              : notification
          ) || []
      );

      return { previousNotifications };
    },
    onError: (err, notificationId, context) => {
      // Rollback on error
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          [QUERIES.NOTIFICATIONS, userId],
          context.previousNotifications
        );
      }
      // Refetch to confirm server state after rollback
      queryClient.invalidateQueries({
        queryKey: [QUERIES.NOTIFICATIONS, userId],
      });
      captureError(err, {
        context: "useNotification.markAsShown",
        notificationId,
      });
    },
    // No onSettled: optimistic updates handle the success path immediately,
    // and WS notification_updated events drive any server-side re-sync.
  });

  return {
    // Core data (all computed in select, fully memoized)
    notifications: notificationData.notifications,
    chatNotifications: notificationData.chatNotifications,
    tradeNotifications: notificationData.tradeNotifications,

    // Counts and flags
    unreadCount: notificationData.unreadCount,
    hasUnreadNotifications: notificationData.hasUnreadNotifications,
    hasUnreadChatNotifications: notificationData.hasUnreadChatNotifications,
    hasUnreadTradeNotifications: notificationData.hasUnreadTradeNotifications,

    // Latest notifications
    latestChatNotification: notificationData.latestChatNotification,

    // Chat-specific backward compatibility
    unreadCountsByChatId: notificationData.unreadCountsByChatId,
    getUnreadCount,
    hasUnreadMessages,
    getTotalUnreadCount,

    // New structures
    notificationCounts: notificationData.notificationCounts,
    notificationGroups: notificationData.notificationGroups,

    // Unified notification marking functions
    markAsRead: (notificationId: string) =>
      markNotificationMutation.mutate(notificationId),
    markAsReadAsync: (notificationId: string) =>
      markNotificationMutation.mutateAsync(notificationId),
    markAllAsRead: (notificationIds: string[]) =>
      markNotificationMutation.mutate(notificationIds),
    markAllAsReadAsync: (notificationIds: string[]) =>
      markNotificationMutation.mutateAsync(notificationIds),
    isMarkingAsRead: markNotificationMutation.isPending,
    markError: markNotificationMutation.error,

    // Mark as shown functions (with optimistic updates)
    markAsShown: (notificationId: string) =>
      markShownMutation.mutate(notificationId),
    markAsShownAsync: (notificationId: string) =>
      markShownMutation.mutateAsync(notificationId),
    isMarkingAsShown: markShownMutation.isPending,
  };
};
